import React, { useState, useEffect } from "react";
import ExaminerNavbar from "./ExaminerNavbar";
import ExaminerTopHeader from "./ExaminerTopHeader";
import {
  getAllBatchesAPI,
  getSubjectsByBatchAPI,
  getStudentsByBatchAPI,
  updateStudentMarksAPI,
  downloadMarksCSVTemplateAPI,
  bulkUpdateMarksFromCSVAPI,
  toggleMarksVisibilityAPI,
} from "../../utils/Api";
import ToggleSwitch from "../../components/ToggleSwitch";

const ExaminerMarkEntry = () => {
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [marksVisible, setMarksVisible] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        setLoading(true);
        const response = await getAllBatchesAPI();
        setBatches(response.data.batches);
      } catch (err) {
        setError("Failed to fetch batches");
      } finally {
        setLoading(false);
      }
    };
    fetchBatches();
  }, []);

  const handleBatchChange = async (batchId) => {
    const batch = batches.find((b) => b._id === batchId);
    setSelectedBatch(batch);
    setMarksVisible(batch ? batch.marksVisible : false);
    setSelectedSubject(null);
    setSubjects([]);
    setStudents([]);
    if (batchId) {
      try {
        setLoading(true);
        const response = await getSubjectsByBatchAPI(batchId);
        setSubjects(response.data.subjects);
      } catch (err) {
        setError("Failed to fetch subjects");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleVisibility = async () => {
    if (!selectedBatch) return;
    try {
      const response = await toggleMarksVisibilityAPI(
        selectedBatch._id,
        !marksVisible,
      );
      setMarksVisible(response.data.batch.marksVisible);
    } catch (error) {
      alert("Failed to update visibility.");
    }
  };

  const handleSubjectChange = async (subjectId) => {
    const subject = subjects.find((s) => s._id === subjectId);
    setSelectedSubject(subject);
    setStudents([]);
    if (selectedBatch && subject) {
      try {
        setLoading(true);
        const response = await getStudentsByBatchAPI(selectedBatch._id);

        const studentsWithMarks = response.data.students.map((student) => {
          const subjectData = student.academicDetails.subjects.find(
            (s) => s.subject === subjectId,
          );
          const marks = {};
          
          // Initialize internal marks components
          marks["Viva-voce"] = subjectData?.marks.find((m) => m.schemeName === "Viva-voce")?.obtainedMarks || "";
          marks["Attendance"] = subjectData?.marks.find((m) => m.schemeName === "Attendance")?.obtainedMarks || "";
          marks["Class Participation"] = subjectData?.marks.find((m) => m.schemeName === "Class Participation")?.obtainedMarks || "";
          marks["Assignment"] = subjectData?.marks.find((m) => m.schemeName === "Assignment")?.obtainedMarks || "";
          marks["External"] = subjectData?.marks.find((m) => m.schemeName === "External")?.obtainedMarks || "";
          
          return { ...student, marks };
        });

        setStudents(studentsWithMarks);
      } catch (err) {
        setError("Failed to fetch students");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMarkChange = (studentId, schemeName, value) => {
    setStudents(
      students.map((student) =>
        student._id === studentId
          ? { ...student, marks: { ...student.marks, [schemeName]: value } }
          : student,
      ),
    );
  };

  const handleSaveMarks = async (studentId) => {
    const student = students.find((s) => s._id === studentId);
    const marks = ["Viva-voce", "Attendance", "Class Participation", "Assignment", "External"]
      .map((schemeName) => ({
        schemeName,
        obtainedMarks: student.marks[schemeName] || 0,
      }))
      .filter((m) => m.obtainedMarks !== "");

    try {
      await updateStudentMarksAPI({
        studentId,
        subjectId: selectedSubject._id,
        marks,
      });
      alert("Marks saved successfully!");
    } catch (error) {
      alert("Failed to save marks.");
    }
  };

  const toggleRowExpansion = (studentId) => {
    setExpandedRows(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const calculateInternalTotal = (student, internalBreakdown) => {
    if (!internalBreakdown) return 0;
    return Object.keys(internalBreakdown).reduce((sum, name) => {
      return sum + (parseFloat(student.marks[name]) || 0);
    }, 0);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedBatch || !selectedSubject) {
      alert("Please select a batch and subject first.");
      return;
    }
    try {
      const response = await downloadMarksCSVTemplateAPI(
        selectedBatch._id,
        selectedSubject._id,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `marks-template-${selectedSubject.subjectCode}.csv`,
      );
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      alert("Failed to download template.");
    }
  };

  const handleFileUpload = async () => {
    if (!file || !selectedSubject) {
      alert("Please select a file and subject.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);

    try {
      await bulkUpdateMarksFromCSVAPI(selectedSubject._id, formData);
      alert("Marks uploaded successfully!");
      // Refresh students list
      handleSubjectChange(selectedSubject._id);
    } catch (error) {
      alert("Failed to upload marks.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ExaminerNavbar />
      <ExaminerTopHeader />
      <div className="lg:ml-64 transition-all duration-300 flex flex-col">
        <div className="pt-20 min-h-screen text-black">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 max-w-6xl mx-auto mt-10">
            <h1 className="text-3xl font-bold mb-6">Mark Entry</h1>

            <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Select Batch
                  </label>
                  <select
                    value={selectedBatch?._id || ""}
                    onChange={(e) => handleBatchChange(e.target.value)}
                    className="w-full p-2 border-2 border-blue-500 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">-- Select a Batch --</option>
                    {batches.map((batch) => (
                      <option key={batch._id} value={batch._id}>
                        {batch.batchName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Select Subject
                  </label>
                  <select
                    value={selectedSubject?._id || ""}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    className="w-full p-2 border-2 border-blue-500 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                    disabled={!selectedBatch}
                  >
                    <option value="">-- Select a Subject --</option>
                    {subjects.map((subject) => (
                      <option key={subject._id} value={subject._id}>
                        {subject.subjectName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {selectedBatch && (
                <div className="mt-4 flex items-center">
                  <label className="mr-4 font-medium">
                    Make Marks Visible to Students:
                  </label>
                  <ToggleSwitch
                    isToggled={marksVisible}
                    onToggle={handleToggleVisibility}
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            {loading && <p>Loading...</p>}

            {selectedSubject && (
              <div className="my-4">
                <button
                  onClick={handleDownloadTemplate}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Download CSV Template
                </button>
                <div className="mt-4">
                  <input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                  <button
                    onClick={handleFileUpload}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 ml-2"
                  >
                    Upload CSV
                  </button>
                </div>
              </div>
            )}

            {students.length > 0 && selectedSubject && (
              <div>
                {/* Display marking scheme info */}
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold mb-2 text-gray-800">Marking Scheme:</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-700">
                    {(() => {
                      const totalInternal = selectedSubject.markingScheme
                        .filter(scheme => scheme.name !== "External")
                        .reduce((sum, scheme) => sum + scheme.value, 0);
                      const externalScheme = selectedSubject.markingScheme.find(s => s.name === "External");
                      
                      const internalBreakdown = totalInternal === 25 ? {
                        "Viva-voce": 5,
                        "Attendance": 5,
                        "Class Participation": 5,
                        "Assignment": 10,
                      } : totalInternal === 40 ? {
                        "Viva-voce": 10,
                        "Attendance": 10,
                        "Class Participation": 10,
                        "Assignment": 10,
                      } : null;

                      return (
                        <>
                          {internalBreakdown && (
                            <>
                              <div>Viva-voce: {internalBreakdown["Viva-voce"]} marks</div>
                              <div>Attendance: {internalBreakdown["Attendance"]} marks</div>
                              <div>Class Participation: {internalBreakdown["Class Participation"]} marks</div>
                              <div>Assignment: {internalBreakdown["Assignment"]} marks</div>
                              <div className="font-semibold text-blue-700">Total Internal: {totalInternal} marks</div>
                            </>
                          )}
                          {externalScheme && (
                            <div className="font-semibold text-blue-700">External: {externalScheme.value} marks</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-blue-600 text-white">
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Roll No
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Student Name
                        </th>
                        {(() => {
                          const totalInternal = selectedSubject.markingScheme
                            .filter(scheme => scheme.name !== "External")
                            .reduce((sum, scheme) => sum + scheme.value, 0);
                          
                          return (
                            <th className="border border-gray-300 px-4 py-2 text-left">
                              Internal ({totalInternal})
                            </th>
                          );
                        })()}
                        {(() => {
                          const externalScheme = selectedSubject.markingScheme.find(s => s.name === "External");
                          return externalScheme && (
                            <th className="border border-gray-300 px-4 py-2 text-left">
                              External ({externalScheme.value})
                            </th>
                          );
                        })()}
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => {
                        const totalInternal = selectedSubject.markingScheme
                          .filter(scheme => scheme.name !== "External")
                          .reduce((sum, scheme) => sum + scheme.value, 0);
                        const externalScheme = selectedSubject.markingScheme.find(s => s.name === "External");
                        
                        const internalBreakdown = totalInternal === 25 ? {
                          "Viva-voce": 5,
                          "Attendance": 5,
                          "Class Participation": 5,
                          "Assignment": 10,
                        } : totalInternal === 40 ? {
                          "Viva-voce": 10,
                          "Attendance": 10,
                          "Class Participation": 10,
                          "Assignment": 10,
                        } : null;

                        const isExpanded = expandedRows[student._id];
                        const internalTotal = calculateInternalTotal(student, internalBreakdown);

                        return (
                          <React.Fragment key={student._id}>
                            <tr className="bg-white hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-2">
                                {student.academicDetails.rollNumber}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {`${student.studentDetails.firstName} ${student.studentDetails.lastName}`}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleRowExpansion(student._id)}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium text-sm"
                                  >
                                    {isExpanded ? '▼ Hide' : '▶ Show'} Breakdown
                                  </button>
                                  <span className="font-semibold text-gray-700">
                                    Total: {internalTotal} / {totalInternal}
                                  </span>
                                </div>
                              </td>
                              {externalScheme && (
                                <td className="border border-gray-300 px-4 py-2">
                                  <input
                                    type="number"
                                    value={student.marks["External"]}
                                    onChange={(e) =>
                                      handleMarkChange(
                                        student._id,
                                        "External",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full p-2 border-2 border-blue-500 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    min="0"
                                    max={externalScheme.value}
                                  />
                                </td>
                              )}
                              <td className="border border-gray-300 px-4 py-2">
                                <button
                                  onClick={() => handleSaveMarks(student._id)}
                                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                >
                                  Save
                                </button>
                              </td>
                            </tr>
                            {isExpanded && internalBreakdown && (
                              <tr className="bg-blue-50">
                                <td colSpan="5" className="border border-gray-300 px-4 py-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {Object.entries(internalBreakdown).map(([name, maxValue]) => (
                                      <div key={name} className="flex flex-col">
                                        <label className="text-sm font-semibold text-gray-700 mb-1">
                                          {name} (Max: {maxValue})
                                        </label>
                                        <input
                                          type="number"
                                          value={student.marks[name]}
                                          onChange={(e) =>
                                            handleMarkChange(
                                              student._id,
                                              name,
                                              e.target.value,
                                            )
                                          }
                                          className="p-2 border-2 border-blue-500 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                                          min="0"
                                          max={maxValue}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExaminerMarkEntry;
